import { useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ApiError } from '@/lib/api';
import {
  PRESET_CODES,
  PRESET_LABELS,
  slugifyRoleCode,
  validateRoleName,
  type PresetCode,
  type RoleDetail,
  type RoleSummary,
} from '@/pages/company/roles/rolesLogic';
import { PRESET_ROLE_CATALOG } from '@/pages/company/roles/presetCatalog';
import { useCreateRoleFromPreset } from '@/pages/company/roles/hooks/useRolesQueries';

interface CreateFromPresetDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Предвыбранный пресет (из дропдауна «Создать из пресета ▾»). */
  initialPreset?: PresetCode;
  customRoles: RoleSummary[];
  /** Открыть созданную роль в редакторе для правки предзаполненной матрицы. */
  onCreated?: (role: RoleDetail) => void;
}

/**
 * CreateFromPresetDrawer — создание кастомной роли из пресета (фрейм 3, AK-343).
 *
 * Пресет-основа (Агрохолдинг/Колхоз) → роль-шаблон → имя → POST /from-preset.
 * Матрица предзаполняется на бэке снимком пресета (preset_version фиксируется);
 * созданную роль сразу открываем в редакторе, чтобы админ подправил и увидел
 * права словами. Пресет копируется снимком — обновление шаблона не трогает
 * уже созданные роли (версионирование).
 */
export function CreateFromPresetDrawer({
  open,
  onOpenChange,
  initialPreset = 'kolkhoz',
  customRoles,
  onCreated,
}: CreateFromPresetDrawerProps) {
  const createMut = useCreateRoleFromPreset();

  const [preset, setPreset] = useState<PresetCode>(initialPreset);
  const [presetRole, setPresetRole] = useState<string>('');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | undefined>();

  const roleOptions = useMemo(() => PRESET_ROLE_CATALOG[preset], [preset]);

  useEffect(() => {
    if (!open) return;
    setPreset(initialPreset);
    setPresetRole('');
    setName('');
    setNameError(undefined);
    setSubmitError(undefined);
  }, [open, initialPreset]);

  // При смене пресета — сброс выбранной роли (у пресетов разные наборы).
  useEffect(() => {
    setPresetRole('');
  }, [preset]);

  const selectedRole = roleOptions.find((r) => r.code === presetRole);

  const runNameValidation = (value: string): boolean => {
    const v = validateRoleName(value, [], null, customRoles);
    setNameError(v.error);
    return v.ok;
  };

  const submit = async () => {
    setSubmitError(undefined);
    if (!presetRole) {
      setSubmitError('Выберите роль-шаблон из пресета');
      return;
    }
    if (!runNameValidation(name)) return;
    try {
      const created = await createMut.mutateAsync({
        preset_code: preset,
        preset_role_code: presetRole,
        code: slugifyRoleCode(name),
        name: name.trim(),
      });
      onOpenChange(false);
      onCreated?.(created);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 422 || e.status === 409)) {
        setNameError(e.detail);
      } else {
        setSubmitError(e instanceof ApiError ? e.detail : 'Не удалось создать роль');
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-[560px]">
        <SheetTitle>Новая роль из пресета</SheetTitle>
        <SheetDescription>
          Пресет-основа задаёт стартовые права. После создания откроется редактор —
          поправьте под своё хозяйство и сохраните.
        </SheetDescription>

        <div className="mt-6 space-y-5">
          <div className="space-y-2">
            <Label>Пресет-основа</Label>
            <RadioGroup
              value={preset}
              onValueChange={(v) => setPreset(v as PresetCode)}
              className="gap-0 overflow-hidden rounded-lg border border-border"
            >
              {PRESET_CODES.map((code, i) => (
                <label
                  key={code}
                  htmlFor={`preset-${code}`}
                  className={
                    'flex cursor-pointer items-start gap-3 px-3 py-3 ' +
                    (i > 0 ? 'border-t border-border/60 ' : '') +
                    (preset === code ? 'bg-accent-brand/10' : '')
                  }
                >
                  <RadioGroupItem id={`preset-${code}`} value={code} className="mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold">{PRESET_LABELS[code].name}</div>
                    <div className="text-xs text-muted-foreground">
                      {code === 'agroholding'
                        ? 'Централизованно: поля Read, операции согласуются — R-центрично'
                        : 'Автономно на местах: агроном ведёт поля и осмотры сам — CRUD-центрично'}
                    </div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Роль-шаблон</Label>
            <RadioGroup
              value={presetRole}
              onValueChange={setPresetRole}
              className="gap-0 overflow-hidden rounded-lg border border-border"
            >
              {roleOptions.map((r, i) => (
                <label
                  key={r.code}
                  htmlFor={`prole-${r.code}`}
                  className={
                    'flex cursor-pointer items-start gap-3 px-3 py-2.5 ' +
                    (i > 0 ? 'border-t border-border/60 ' : '') +
                    (presetRole === r.code ? 'bg-accent-brand/10' : '')
                  }
                >
                  <RadioGroupItem id={`prole-${r.code}`} value={r.code} className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.description}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="preset-role-name">
              Название вашей роли <span className="text-destructive">*</span>
            </Label>
            <Input
              id="preset-role-name"
              value={name}
              placeholder={selectedRole ? `например, ${selectedRole.name}-полевик` : 'например, Агроном-полевик'}
              aria-invalid={!!nameError}
              className={nameError ? 'border-destructive' : undefined}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) runNameValidation(e.target.value);
              }}
              onBlur={(e) => name && runNameValidation(e.target.value)}
            />
            {nameError ? (
              <p className="text-xs text-destructive">{nameError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Имя не должно совпадать с системными ролями.
              </p>
            )}
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Пресет копируется <b>снимком</b> — если мы позже обновим шаблон, ваша роль
              не изменится.
            </AlertDescription>
          </Alert>

          {submitError && (
            <Alert variant="destructive">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="mt-6 flex gap-3 border-t border-border pt-4">
          <Button
            variant="ghost"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={createMut.isPending}
          >
            Отмена
          </Button>
          <Button
            className="flex-1"
            onClick={submit}
            disabled={createMut.isPending || !presetRole || !name.trim()}
          >
            {createMut.isPending ? 'Создаём…' : 'Создать роль'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
